import { Bounding } from '../../core/models/bounding';
import { Element } from '../../equipment/models/elements/element';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { ExtensionType } from '../../equipment/models/elements/extension';
import { IProductPacking } from '../../product/models/product-card';
import { GlobalSettings } from '../../core/models/globalSettings';
import { Point } from '../../core/models/point';
import { Product } from './product';
import { ProductGroupRotation } from './product-group-rotation';
import { Rectangle } from '../../equipment/models/figures/rectangle';
import { Trapeze } from '../../equipment/models/figures/trapeze';

import { Utils } from '../../core/services/utils.service';
import * as jsts from 'jsts/dist/jsts';
import { DEFAULT_PERSONAL_SETTINGS, ProductRotationPointType } from '../../core/models/personalSettings';
import { UnitConverter } from '../../core/utils/unitConverter';
import { DefaultProductParams } from '../../core/models/defaultProductParams';

export enum ProductErrors {
	ShelfLeftLimit = 'ShelfLeftLimit',
	ShelfRightLimit = 'ShelfRightLimit',
	BoxLeftLimit = 'BoxLeftLimit',
	BoxRightLimit = 'BoxRightLimit',
	HangBarLeftLimit = 'HangBarLeftLimit',
	HangBarRightLimit = 'HangBarRightLimit',
	ShelfDepth = 'ShelfDepth',
	BoxDepth = 'BoxDepth',
	HookDepth = 'HookDepth',
	ElementHeight = 'ElementHeight',
	TrapezeLimits = 'TrapezeLimits',
	DependLimits = 'DependLimits',
	DependDepth = 'DependDepth',
	CentralPillarIntersection = 'CentralPillarIntersection',
	PartitionIntersection = 'PartitionIntersection',
	SideWallsIntersection = 'SideWallsIntersection',
	BoxIntersection = 'BoxIntersection',
	ProductIntersection = 'ProductIntersection',
	LooseOnShelf = 'LooseOnShelf'
}

const GEOMETRY_FACTORY: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory(),
	Y_EPSILON: number = .02,
	EPS_3: number = 1e-3,
	LOOSE_PRODUCT_HEIGHT: number = .04;

interface ProductGroupParams {
	id: number,
	begin: any,
	qty: number,
	placementDepth: number,
	placementHeight: number,
	rotation: ProductGroupRotation,
	product: Product,
	selectPacking: number,
	element: Element,
	block: number,
	height_compression_coef: number;
	width_compression_coef: number;
	depth_compression_coef: number;
	dependProduct: ProductGroup,
	applied_compressions: Array<CompressionType>,
	display: boolean,
	weight: number,
	angle: number,
	detailedRotationPoint: ProductRotationPointType
}

export enum CompressionType {
    Depth = 'depth',
    Height = 'height',
    Width = 'width'
}

type FrontVisibleSide = {
	side: 'left' | 'right' | 'front' | 'back';
	width: number;
	xMin: number;
}

export class ProductGroup {
	id: number;
	begin: Point;
	qty: number = 1;
	placementDepth: number = 1;
	placementHeight: number = 1;
	rotation: ProductGroupRotation = ProductGroupRotation.Front;
	product: Product;
	selectPacking: number = null;
	element: Element;
	block: number;
	error: boolean = false;
	errorTypes: Array<string> = [];
	applied_compressions: Array<CompressionType> = [];
	height_compression_coef: number;
	width_compression_coef: number;
	depth_compression_coef: number;
	weight: number = new DefaultProductParams().weight;
	private _angle: number = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
	private _dependProduct: ProductGroup = null;
	private _dependentProduct: ProductGroup = null;
	private _detailedRotationPoint: ProductRotationPointType = DEFAULT_PERSONAL_SETTINGS.productRotationPoint;

	static imageNotFound(src): boolean {
		return src.split('/').slice(-1) == 'img404.jpg';
	}

	static getHasAssortment(): boolean {
		return JSON.parse(localStorage.getItem('defaults')).has_assortment;
	}

	get autoDepth(): boolean {
		if (this.element.canRotateProductGroup(this)) return false;

		return this.element.getMaxPlacementDepth(this) == this.placementDepth;
	}

	get availability(): boolean {
		return !(this.element.hasExtension(ExtensionType.Availability) && !this.element.isExtensionActive(ExtensionType.Availability));
	}

	get angle(): number {
		return this._angle;
	}

	set angle(value: number) {
		if (this.angle === value) return;

		const rotationPoint: Point = this.detailedRotationPoint;

		// the point where the product would start if the rotation angle were 0
		const baseCorePoint: [number, number] = Utils.rotatePoint(
			[rotationPoint.x, rotationPoint.y],
			[this.begin.x, this.begin.z],
			-this.angle
		);

		// the point where the product will start after changing the angle of rotation
		const newCorePoint: [number, number] = Utils.rotatePoint(
			[rotationPoint.x, rotationPoint.y],
			baseCorePoint,
			value
		);

		this.begin.x = newCorePoint[0];
		this.begin.z = newCorePoint[1];
		this._angle = value;

        if (this.dependentProduct) {
            this.dependentProduct.begin.x = newCorePoint[0];
		    this.dependentProduct.begin.z = newCorePoint[1];
		    this.dependentProduct._angle = value;
        }
	}

	get dependProduct(): ProductGroup {
		return this._dependProduct;
	}

	set dependProduct(dependProduct: ProductGroup) {
		if (this._dependProduct) this._dependProduct.dependentProduct = null;
		if (dependProduct) {
			dependProduct.dependentProduct = this;
			this.begin.x = dependProduct.begin.x;
			this.begin.y = dependProduct.getBoundingY().max;
			this.begin.z = dependProduct.begin.z;
            this._angle = dependProduct.angle;
		}

		this._dependProduct = dependProduct;
	}

	get dependentProduct(): ProductGroup {
		return this._dependentProduct;
	}

	set dependentProduct(dependentProduct: ProductGroup) {
		this._dependentProduct = dependentProduct;
	}

	get detailedRotationPoint(): Point {
		const angleRad: number = (this.angle * Math.PI) / 180;
		const cosAngle: number = Math.cos(angleRad);
		const sinAngle: number = Math.sin(angleRad);

		switch (this._detailedRotationPoint) {
			case 'bottomLeft': {
				return { x: this.begin.x, y: this.begin.z };
			}
			case 'topLeft': {
				const offsetX = -this.Depth * sinAngle;
				const offsetY = this.Depth * cosAngle;

				const xTopLeft = this.begin.x - offsetX;
				const yTopLeft = this.begin.z - offsetY;

				return { x: xTopLeft, y: yTopLeft };
			}
			case 'center': {
				const halfWidth = this.Width / 2;
				const halfHeight = -this.Depth / 2;

				const xCenter = this.begin.x + (halfWidth * cosAngle - halfHeight * sinAngle);
				const yCenter = this.begin.z + (halfWidth * sinAngle + halfHeight * cosAngle);

				return { x: xCenter, y: yCenter };
			}
		}
	}

	get depthCompressionCoef(): number {
		return this.depth_compression_coef ?? this.product.depth_compression_coef;
	}

	get displayPlacementDepth(): number {
		return this.isLooseForm ? 1 : Math.min(this.placementDepth, this.qty);
	}

	get displayPlacementHeight(): number {
		return this.isLooseForm ? 1 : this.placementHeight;
	}

	set Element(value: Element) {
		const elementDepth = this.dependProduct ? this.dependProduct.Depth : value.Depth,
			oldFaces = this.FacesQty,
			placement = this.applied_compressions.includes(CompressionType.Depth) ?
				Math.floor(elementDepth / this.ItemDepth) :
				Math.floor(elementDepth / (this.isLooseForm ? value.Depth : this.product.Depth(this.rotation)));

		if (placement < this.placementDepth) {
			this.placementDepth = Math.max(placement, 1);
			this.qty = this.placementDepth * oldFaces;
		}

		value.addProduct(this);
	}

	get FacesQty() {
		return this.isLooseForm ? 1 : Math.ceil(this.qty / this.placementDepth);
	}

	set FacesQty(faces) {
		this.qty = this.placementDepth * faces;
	}

	get frontVisibleSides(): Array<FrontVisibleSide> {
		const [leftFront, rightFront, rightBack, leftBack] = this.zCoordinates;
		const visibleSides: Array<FrontVisibleSide> = [];
		const boundingX: Bounding = this.getBoundingX();

		if (leftFront.y > leftBack.y && rightFront.y > rightBack.y) {
			visibleSides.push({
				side: 'front',
				width: Utils.round(Math.abs(rightFront.x - leftFront.x)),
				xMin: Math.min(rightFront.x, leftFront.x) - boundingX.min
			});
		} else if (leftFront.y < leftBack.y && rightFront.y < rightBack.y) {
			visibleSides.push({
				side: 'back',
				width: Utils.round(Math.abs(rightBack.x - leftBack.x)),
				xMin: Math.min(rightBack.x, leftBack.x) - boundingX.min
			});
		}

		if (rightFront.y > leftFront.y && rightBack.y > leftBack.y) {
			visibleSides.push({
				side: 'right',
				width: Utils.round(Math.abs(rightBack.x - rightFront.x)),
				xMin: Math.min(rightBack.x, rightFront.x) - boundingX.min
			});
		} else if (rightFront.y < leftFront.y && rightBack.y < leftBack.y) {
			visibleSides.push({
				side: 'left',
				width: Utils.round(Math.abs(leftBack.x - leftFront.x)),
				xMin: Math.min(leftBack.x, leftFront.x) - boundingX.min
			});
		}

		return visibleSides
			.filter((side: FrontVisibleSide) => side.width)
			.sort((a, b) => a.xMin - b.xMin);
	}

	get heightCompressionCoef(): number {
		return this.height_compression_coef ?? this.product.height_compression_coef;
	}

	get image(): { img: HTMLImageElement, rotation: number } {
		const selectedPacking: IProductPacking = this.product.packings.find((packing: IProductPacking) =>
				packing.id == this.selectPacking || packing == (this.selectPacking as any)
			),
			img: HTMLImageElement = selectedPacking?.image || this.product.image;

		let rotation: number;
		switch (this.rotation) {
			case ProductGroupRotation.FrontRotatedRight: rotation = 90; break;
			case ProductGroupRotation.FrontRotatedLeft: rotation = -90; break;
			default: rotation = 0;
		}

		return {
			img: img,
			rotation: rotation
		}
	}

	get inactive() {
		return !this.product.is_active && ProductGroup.getHasAssortment();
	}

	get isLooseForm(): boolean {
		return this.product.is_loose && Utils.isNullOrUndefined(this.selectPacking);
	}

	get ItemDepth() {
		if (this.isLooseForm) return this.element.Depth;

		let { width, height, depth } = this.product,
			packingParam = this.returnPackingParam(),
			compression_coef = 1 - this.depthCompressionCoef,
			is_need_apply_compression =
				!this.isLooseForm &&
				this.applied_compressions.includes(CompressionType.Depth);

		if (packingParam != null) {
			width = packingParam.width;
			height = packingParam.height;
			depth = packingParam.depth;
		}

		switch (this.rotation) {
			case ProductGroupRotation.Side: {
				return Utils.round(width *= is_need_apply_compression ? compression_coef : 1);
			}
			case ProductGroupRotation.Top: {
				return Utils.round(height *= is_need_apply_compression ? compression_coef : 1);
			}
			default: {
				return Utils.round(depth *= is_need_apply_compression ? compression_coef : 1);
			}
		}
	}

	get ItemHeight() {
		if (this.isLooseForm && ['Box', 'SlicedBox'].includes(this.element.parent.type)) {
			return this.element.Height;
		}
		if (this.isLooseForm && !['Hook', 'Hole'].includes(this.element.parent.type)) return LOOSE_PRODUCT_HEIGHT;

		let { width, height, depth } = this.product,
			packingParam = this.returnPackingParam(),
			compression_coef = 1 - this.heightCompressionCoef,
			is_need_apply_compression =
				!this.isLooseForm &&
				this.applied_compressions.includes(CompressionType.Height);

		if (packingParam != null) {
			width = packingParam.width;
			height = packingParam.height;
			depth = packingParam.depth;
		}

		switch (this.rotation) {
			case ProductGroupRotation.Top: {
				return depth *= is_need_apply_compression ? compression_coef : 1;
			}
			case ProductGroupRotation.FrontRotatedRight:
			case ProductGroupRotation.FrontRotatedLeft: {
				return width *= is_need_apply_compression ? compression_coef : 1;
			}
			default: {
				return height *= is_need_apply_compression ? compression_coef : 1;
			}
		}
	}

	get ItemWidth() {
		if (this.isLooseForm && ['Box', 'SlicedBox'].includes(this.element.parent.type)) {
			return Utils.round(this.element.Width - (this.element.shapes[0].wallWidth * 2));
		}
		if (this.isLooseForm && !['Hook', 'Hole'].includes(this.element.parent.type)) return this.element.Width;

		let { width, height, depth } = this.product,
			packingParam = this.returnPackingParam(),
			compression_coef = 1 - this.widthCompressionCoef,
			is_need_apply_compression =
				!this.isLooseForm &&
				this.applied_compressions.includes(CompressionType.Width);

		if (packingParam != null) {
			width = packingParam.width;
			height = packingParam.height;
			depth = packingParam.depth;
		}

		switch (this.rotation) {
			case ProductGroupRotation.Side: {
				return depth *= is_need_apply_compression ? compression_coef : 1;
			}
			case ProductGroupRotation.FrontRotatedRight:
			case ProductGroupRotation.FrontRotatedLeft: {
				return height *= is_need_apply_compression ? compression_coef : 1;
			}
			default: {
				return width *= is_need_apply_compression ? compression_coef : 1;
			}
		}
	}

	get Depth() {
		return Utils.round((this.isLooseForm ? 1 : Math.min(this.placementDepth, this.qty)) * this.ItemDepth);
	}

	get depth(): number { // глибина productGroup після повороту
		const zCoordinates: Array<number> = this.zCoordinates.map((c: Point) => c.y);
		return Utils.round(Math.max(...zCoordinates) - Math.min(...zCoordinates));
	}

	get getPackingQty() {
		const packing = this.product.packings.find(p => p.id == this.selectPacking);

		if (!this.selectPacking || !packing) {
			return this.product.is_loose ?
				UnitConverter.getConvertValue(this.weight, 'mass', 'inches', 3)
				: this.placementDepth * this.placementHeight * this.FacesQty;
		}

		return (
			this.product.is_loose
				? UnitConverter.getConvertValue(packing.weight, 'mass', 'inches', 3)
				: packing.qty
			) * this.placementDepth * this.placementHeight * this.FacesQty;
	}

	get getPlacementHeight() {
		return this.isLooseForm ? 1 : this.placementHeight;
	}

	get getPlacementDepth() {
		return this.isLooseForm ? 1 : this.placementDepth;
	}

	get Height() {
		return Utils.round((this.isLooseForm ? 1 : this.placementHeight) * this.ItemHeight);
	}

	get number() {
		return this.element.sortedProducts.indexOf(this) + 1;
	}

	get avgPrice(): number {
		return this.product.avgPrice;
	}

	get price() {
		return this.product.price;
	}

	get Width() {
		return Utils.round((this.isLooseForm ? 1 : this.FacesQty) * this.ItemWidth);
	}

	get width(): number { // ширина productGroup після повороту
		const xCoordinates: Array<number> = this.zCoordinates.map((c: Point) => c.x);
		return Utils.round(Math.max(...xCoordinates) - Math.min(...xCoordinates));
	}

	get widthCompressionCoef(): number {
		return this.width_compression_coef ?? this.product.width_compression_coef;
	}

	get zCoordinates(): Array<Point> {
		const angleRad: number = (this.angle * Math.PI) / 180;
		const cosAngle: number = Math.cos(angleRad);
		const sinAngle: number = Math.sin(angleRad);
		const rotationPoint: Point = this.detailedRotationPoint;
		let corners: Array<Point>;

		switch (this._detailedRotationPoint) {
			case 'center': {
				const halfWidth = this.Width / 2;
				const halfHeight = -this.Depth / 2;
				corners = [
					{ x: -halfWidth, y: -halfHeight },
					{ x: halfWidth, y: -halfHeight },
					{ x: halfWidth, y: halfHeight },
					{ x: -halfWidth, y: halfHeight },
					{ x: -halfWidth, y: -halfHeight }
				];
				break;
			}
			case 'topLeft': {
				corners = [
					{ x: 0, y: this.Depth },
					{ x: this.Width, y: this.Depth },
					{ x: this.Width, y: 0 },
					{ x: 0, y: 0 },
					{ x: 0, y: this.Depth }
				];
				break;
			}
			case 'bottomLeft': {
				corners = [
					{ x: 0, y: 0 },
					{ x: this.Width, y: 0 },
					{ x: this.Width, y: -this.Depth },
					{ x: 0, y: -this.Depth },
					{ x: 0, y: 0 }
				];
				break;
			}
		}

		return corners.map((corner: Point) => {
			const xRot = Utils.round(rotationPoint.x + (corner.x * cosAngle - corner.y * sinAngle));
			const yRot = Utils.round(rotationPoint.y + (corner.x * sinAngle + corner.y * cosAngle));
			return { x: xRot, y: yRot };
		});
	}

	get zMax() {
		if (
			this.element.shapes[0].type == 'trapeze'
			&& this.element.shapes[0].frontWidth < this.element.shapes[0].backWidth
		) {
			let z: number = this.element.getAbsoluteBoundingZ().max;

			const shape: Trapeze = this.element.shapes[0] as Trapeze;
			const bounding: Bounding = shape.getBoundingX(true);

			if (this.getBoundingX().max > bounding.max) {
				z = (shape.getBoundingX().max - this.getBoundingX().max) / shape.tan_a - EPS_3;
			}

			if (this.getBoundingX().min < bounding.min) {
				z = (this.getBoundingX().min - shape.begin.x) / shape.tan_a - EPS_3;
			}

			if (z < shape.begin.z) {
				z = shape.begin.z;
			}

			return Utils.round(z);
		} else if (this.element.depend?.parent.is(EquipmentElements.CentralPillar)) {
			return Utils.round((this.element.Depth - this.element.depend.Depth) / 2);
		} else if (this.element.relatedElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar))) {
			const centralPillar: Element = this.element.relatedElements
				.find((el: Element) => el.parent.is(EquipmentElements.CentralPillar));

			return Utils.round((this.element.Depth - centralPillar.Depth) / 2);
		}

		return Utils.round(this.element.Depth);
	}

	get zPolygon() {
		return GEOMETRY_FACTORY.createPolygon(this.zCoordinates.map(i => new jsts.geom.Coordinate(i.x, i.y)));
	}

	constructor(params: Partial<ProductGroupParams>) {
		if (params.id) this.id = params.id;
		if (params.qty) this.qty = params.qty;
		if (params.product) this.product = params.product;
		if (params.element) this.element = params.element;
		if (params.begin) this.begin = Object.assign({}, params.begin);
        if (params.hasOwnProperty('angle')) this._angle = params.angle ?? GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
		if (params.rotation) {
            if (params.rotation === ProductGroupRotation.FrontRotated) { // TODO: crutch for new product rotation
                this.rotation = ProductGroupRotation.FrontRotatedRight;
            } else {
                this.rotation = params.rotation;
            }
        }
		if (params.placementHeight) this.placementHeight = params.placementHeight;
		if (params.selectPacking) this.selectPacking = params.selectPacking;
		if (params.block) this.block = params.block;
		if (params.dependProduct) this.dependProduct = params.dependProduct;
		if (params.applied_compressions) this.applied_compressions = [...params.applied_compressions || []];
		if (params.weight) this.weight = params.weight;
		if (params.detailedRotationPoint) this._detailedRotationPoint = params.detailedRotationPoint;

		this.height_compression_coef = params.height_compression_coef;
		this.width_compression_coef = params.width_compression_coef;
		this.depth_compression_coef = params.depth_compression_coef;

		if (!params.hasOwnProperty('display')) params.display = true;
		if (!this.product.image && params.display) this.product.loadImage();

		this.placementDepth = Math.floor(Utils.round(this.element.Depth) / (this.ItemDepth * (1 - this.depthCompressionCoef)));
		this.placementDepth = Math.min(this.placementDepth, this.qty);

		if (this.placementDepth <= 0) this.placementDepth = 1;
		if (this.qty <= 0) this.qty = this.placementDepth;

		this.checkPositionByElement();
	}

	canMove(x?: number, y?: number, z?: number): boolean {
		return !this.dependProduct && this.element.canMoveProductGroup(this, x, y, z);
	}

	checkPositionByElement() {
		if (this.element.parent.type == 'HangBar') return;

		const el_bounding = ['Hook', 'Hole'].includes(this.element.parent.type) ?
				this.element.getBoundingY().min - this.ItemHeight :
				this.element.getBoundingY().max,

			y_diff = this.begin.y - el_bounding,
			is_need_update_y = Math.abs(y_diff) > Y_EPSILON;

		if (!is_need_update_y) return;

		switch (this.element.parent.type) {
			case 'Hook':
			case 'Hole':
				this.begin.y = this.element.getBoundingY().min - this.ItemHeight;
				this.begin.x = this.element.shapes[0].begin.x - (this.ItemWidth - (this.element.shapes[0] as Rectangle).width) / 2;
				break;
			case 'SlicedBox':
			case 'Box':
				if (!this.element.depend) break;
				this.begin.y = this.dependProduct ?
					this.dependProduct.getBoundingY().max :
					this.element.depend.parent.type == 'AngleShelf' ?
						this.element.depend.getBoundingY().max :
						this.element.getBoundingY().min;
				break;
			default:
				this.begin.y = this.dependProduct ?
					this.dependProduct.getBoundingY().max :
					this.element.getBoundingY().max;
		}
	}

	copy(autoDepth: boolean = false, display: boolean = true): ProductGroup {
		const product: ProductGroup = new ProductGroup({
			...this,
			display: display,
			angle: this.angle,
			detailedRotationPoint: this._detailedRotationPoint
		});

		product.placementDepth = this.applied_compressions.includes(CompressionType.Depth) && autoDepth
			? this.element.getMaxPlacementDepth(product)
			: this.placementDepth;

		product.placementDepth = Math.min(product.qty, product.placementDepth) || 1;
		product.begin.y = product.dependProduct ? product.dependProduct.getBoundingY().max : product.begin.y;

		return product;
	}

	getBoundingX(): Bounding {
		const xCoordinates: Array<number> = this.zCoordinates.map((c: Point) => c.x);
		return {
			min: Utils.round(this.isLooseForm ? this.element.layoutBoundingX.min : Math.min(...xCoordinates)),
			max: Utils.round(this.isLooseForm ? this.element.layoutBoundingX.max : Math.max(...xCoordinates))
		};
	}

	getBoundingY(): Bounding {
		return {
			min: Utils.round(this.begin.y),
			max: Utils.round(this.begin.y + this.Height)
		};
	}

	getBoundingZ(): Bounding {
		const zCoordinates: Array<number> = this.zCoordinates.map((c: Point) => c.y);
		return {
			min: Utils.round(this.isLooseForm ? this.element.layoutBoundingZ.min : Math.min(...zCoordinates)),
			max: Utils.round(this.isLooseForm ? this.element.layoutBoundingZ.max : Math.max(...zCoordinates))
		};
	}

	getIdHash() {
		return `${this.product.id}-${JSON.stringify(this.begin)}-${this.FacesQty}-${this.Height}-${this.Depth}`;
	}

	move(x?: number, y?: number, z?: number): void {
		if (x) this.begin.x = Utils.round(this.begin.x + x);
		if (y) this.begin.y = Utils.round(this.begin.y + y);
		if (z) this.begin.z = Utils.round(this.begin.z + z);
		if (this.dependentProduct) this.dependentProduct.move(x, y, z);
	}

	returnPackingParam() {
		if (!this.product.packings) return null;

		return this.product.packings.find(i => i.id == this.selectPacking || i == (this.selectPacking as any));
	}

	rotate() {
		switch (this.rotation) {
			case ProductGroupRotation.Front: this.rotation = ProductGroupRotation.Side; break;
			case ProductGroupRotation.Side: this.rotation = ProductGroupRotation.FrontRotatedRight; break;
            case ProductGroupRotation.FrontRotatedRight: this.rotation = ProductGroupRotation.FrontRotatedLeft; break;
			case ProductGroupRotation.FrontRotatedLeft: this.rotation = ProductGroupRotation.Top; break;
			default: this.rotation = ProductGroupRotation.Front;
		}
	}

	/** a crutch for forcing the product to rotate around the center */
	rotateAroundCenter(angle: number): void {
		const tempPoint: ProductRotationPointType = this._detailedRotationPoint;
		this._detailedRotationPoint = 'center';
		this.angle = angle;
		this._detailedRotationPoint = tempPoint;
	}
}
